import { useGSAP } from '@gsap/react';
import { Flip } from 'gsap/Flip';
import { RefObject, useRef, useState } from 'react';
import { useIntersectionCallbacks } from '@/hooks/useIntersectionCallbacks';
import { gsap } from '@/lib/gsap';
import css from './style.module.scss';
import { TestimonialsSectionDto } from './types';
gsap.registerPlugin(Flip);
const DEFAULT_AUTOPLAY_DELAY = 7;
const ANIMATION_DURATION = 0.7;
interface UseTestimonialsReturn {
  containerRef: RefObject<HTMLElement | null>;
  bgRefs: RefObject<(HTMLDivElement | null)[]>;
  contentRefs: {
    title: RefObject<HTMLHeadingElement | null>;
    description: RefObject<HTMLParagraphElement | null>;
    companyLogo: RefObject<HTMLImageElement | null>;
  };
  activeIndex: number;
  isHovered: boolean;
  setIsHovered: (v: boolean) => void;
  handleSlideClick: (index: number) => void;
  getOrderedSlides: () => {
    slide: TestimonialsSectionDto['testimonials'][0];
    originalIndex: number;
  }[];
}
export const useTestimonials = (testimonials: TestimonialsSectionDto['testimonials'], autoplayDelay: number = DEFAULT_AUTOPLAY_DELAY): UseTestimonialsReturn => {
  const containerRef = useRef<HTMLElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const flipStateRef = useRef<Flip.FlipState | null>(null);
  const animateContentRef = useRef<((nextIndex: number) => void) | null>(null);
  const isAnimatingRef = useRef(false);
  const bgRefs = useRef<(HTMLDivElement | null)[]>([]);
  const contentRefs = {
    title: useRef<HTMLHeadingElement>(null),
    description: useRef<HTMLParagraphElement>(null),
    companyLogo: useRef<HTMLImageElement>(null)
  };
  const {
    contextSafe
  } = useGSAP(() => {
    if (flipStateRef.current) {
      Flip.from(flipStateRef.current, {
        targets: `.${css.listItem}`,
        duration: ANIMATION_DURATION,
        ease: 'power2.inOut',
        absolute: true,
        onComplete: () => {
          const list = containerRef.current?.querySelector(`.${css.list}`) as HTMLElement;
          if (list) list.style.height = '';
        }
      });
      flipStateRef.current = null;
    }
  }, {
    scope: containerRef,
    dependencies: [activeIndex]
  });
  const animateContent = contextSafe((nextIndex: number) => {
    if (isAnimatingRef.current) return;
    isAnimatingRef.current = true;
    const currentBg = bgRefs.current[activeIndex];
    const nextBg = bgRefs.current[nextIndex];
    const tl = gsap.timeline({
      onComplete: () => {
        isAnimatingRef.current = false;
      }
    });
    const contentValues = Object.values(contentRefs);
    contentValues.forEach((ref, index) => {
      if (ref.current) tl.to(ref.current, {
        opacity: 0,
        y: -15,
        duration: 0.25,
        ease: 'power2.in'
      }, index * 0.1);
    });
    if (currentBg) tl.to(currentBg, {
      opacity: 0,
      duration: ANIMATION_DURATION,
      ease: 'power2.inOut'
    }, contentValues.length * 0.1);
    if (nextBg) tl.to(nextBg, {
      opacity: 1,
      duration: ANIMATION_DURATION,
      ease: 'power2.inOut'
    }, contentValues.length * 0.1);
    const switchDelay = contentValues.length * 0.1 + 0.15;
    tl.add(() => {
      const list_0 = containerRef.current?.querySelector(`.${css.list}`) as HTMLElement;
      if (list_0) {
        const bounds = list_0.getBoundingClientRect();
        list_0.style.height = `${bounds.height}px`;
      }
      const listItems = document.querySelectorAll(`.${css.listItem}`);
      listItems.forEach(item => {
        const bounds_0 = item.getBoundingClientRect();
        (item as HTMLElement).style.setProperty('--item-height', `${bounds_0.height}px`);
      });
      flipStateRef.current = Flip.getState(listItems);
      setActiveIndex(nextIndex);
    }, switchDelay);
    Object.values(contentRefs).forEach((ref_0, index_0) => {
      if (ref_0.current) tl.to(ref_0.current, {
        opacity: 1,
        y: 0,
        duration: 0.4,
        ease: 'power2.out'
      }, index_0 * 0.1 + switchDelay);
    });
  });
  animateContentRef.current = animateContent;
  useGSAP(() => {
    const target = `.${css.progressSvg} circle`;
    gsap.killTweensOf(target);
    gsap.set(target, {
      strokeDasharray: 100,
      strokeDashoffset: 100
    });
    const tween = gsap.to(target, {
      strokeDashoffset: 0,
      duration: autoplayDelay,
      ease: 'none',
      onComplete: () => {
        const nextIndex_0 = (activeIndex + 1) % testimonials.length;
        animateContentRef.current?.(nextIndex_0);
      }
    });
    return () => {
      tween.kill();
    };
  }, {
    scope: containerRef,
    dependencies: [activeIndex, autoplayDelay, testimonials.length]
  });
  useGSAP(() => {
    const target_0 = `.${css.progressSvg} circle`;
    if (isHovered || !isInView) gsap.getTweensOf(target_0).forEach(t => t.pause());else gsap.getTweensOf(target_0).forEach(t_0 => t_0.play());
  }, {
    scope: containerRef,
    dependencies: [isHovered, isInView]
  });
  useIntersectionCallbacks([containerRef], {
    onEntry: () => setIsInView(true),
    onLeave: () => setIsInView(false)
  });
  const handleSlideClick = (index_1: number) => {
    if (index_1 !== activeIndex) animateContent(index_1);
  };
  const getOrderedSlides = () => {
    const mapped = testimonials.map((slide, index_2) => ({
      slide,
      originalIndex: index_2
    }));
    return [...mapped.slice(activeIndex), ...mapped.slice(0, activeIndex)];
  };
  return {
    containerRef,
    bgRefs,
    contentRefs,
    activeIndex,
    isHovered,
    setIsHovered,
    handleSlideClick,
    getOrderedSlides
  };
};